import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import {
    cancelGeneralJournal,
    deleteGeneralJournal,
    getGeneralJournals,
} from '../../services/GeneralJournalServices';
import { displayError } from '../../helpers';
import { fetchAndShowGeneralJournalPdf } from '../../utils/generalJournalPdf';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';

function GeneralJournalTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;

        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }

        setIsLoading(true);
        return getGeneralJournals(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        })
            .then((response) => {
                setData({
                    records: (response.general_journal_list || []).map((row) => ({
                        ...row,
                        status_label: row.status ? 'Active' : 'Cancelled',
                        hide_row_edit: row.status ? 'No' : 'Yes',
                        hide_row_cancel: row.status ? 'No' : 'Yes',
                        hide_row_delete: row.status ? 'Yes' : 'No',
                        hide_row_print: 'No',
                    })),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load general journals.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'journal_no', headerName: 'Journal No' },
        { fieldName: 'journal_date', headerName: 'Journal Date' },
        { fieldName: 'total_debit', headerName: 'Total Debit' },
        { fieldName: 'total_credit', headerName: 'Total Credit' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return data.records;
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return data.records.filter((row) => {
            const haystack = Object.values(row)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');
            return terms.every((term) => haystack.includes(term));
        });
    }, [data.records, searchText]);

    const summaryStats = useMemo(() => {
        const activeCount = filteredRecords.filter((row) => row.status).length;
        const totalAmount = filteredRecords.reduce((sum, row) => sum + (parseFloat(row.total_debit) || 0), 0);
        return [
            { label: 'ACTIVE', value: String(activeCount), accent: 'b2b' },
            { label: 'TOTAL DEBIT', value: totalAmount.toFixed(2), accent: 'b2c' },
        ];
    }, [filteredRecords]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, []),
        [salesSettings, plants],
    );

    const onView = (recordId) => {
        navigate(`/GeneralJournalEdit/${recordId}`);
    };

    const onEdit = (recordId) => {
        const row = data.records.find((item) => item.id === recordId);
        if (!row?.status) {
            return;
        }
        navigate(`/GeneralJournal/edit/${recordId}`);
    };

    const onCancel = (recordId, event) => {
        event?.preventDefault();
        Swal.fire({
            title: 'Cancel general journal?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setIsLoading(true);
            cancelGeneralJournal(cookies, recordId)
                .then((response) => {
                    Swal.fire('Cancelled', response.message || 'General journal cancelled.', 'success');
                    return fetchRecords();
                })
                .catch((error) => {
                    displayError(error?.response?.data || error, 'Cancel Failed');
                })
                .finally(() => setIsLoading(false));
        });
    };

    const onDelete = (recordId, event) => {
        event?.preventDefault();
        Swal.fire({
            title: 'Delete cancelled general journal?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setIsLoading(true);
            deleteGeneralJournal(cookies, recordId)
                .then((response) => {
                    Swal.fire('Deleted', response.message || 'General journal deleted.', 'success');
                    return fetchRecords();
                })
                .catch((error) => {
                    displayError(error?.response?.data || error, 'Delete Failed');
                })
                .finally(() => setIsLoading(false));
        });
    };

    const onPrint = (recordId, event) => {
        event?.preventDefault();
        setIsLoading(true);
        fetchAndShowGeneralJournalPdf(cookies, recordId, { title: 'General Journal' })
            .finally(() => setIsLoading(false));
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
            filteredRecords,
            'GeneralJournal',
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of General Journal',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            filteredRecords,
            'GeneralJournal.pdf',
        );
    };

    const handleSettingsSave = (nextSettings) => {
        saveSalesListSettings(cookies.myUserId, nextSettings);
        setSalesSettings(nextSettings);
        setShowSettings(false);
        Swal.fire('Updated!', '', 'success');
    };

    useEffect(() => {
        getUserAllowedPlants(cookies)
            .then((plantList) => {
                setPlants((plantList.plant_list || [])
                    .filter((plant) => plant.status)
                    .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
                setIsInitialized(true);
            })
            .catch(() => setIsLoading(false));
    }, [cookies]);

    useEffect(() => {
        if (!isInitialized || plants.length === 0) {
            return;
        }
        fetchRecords();
    }, [isInitialized, plants.length, salesSettings, activeDateRange, fetchRecords]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="ACCOUNTS"
                    title="List of General Journal"
                    subtitle="Balanced debit and credit entries across ledger accounts."
                    modeLabel="VIEW MODE"
                    recordCount={filteredRecords.length}
                    recordLabel="general journals"
                    summaryStats={summaryStats}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    formLink="/GeneralJournal"
                    addNewLabel="Add New"
                >
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="List of General Journal"
                                columns={columns}
                                data={filteredRecords}
                                onRowView={onView}
                                onRowEdit={onEdit}
                                onRowCancel={onCancel}
                                onRowDelete={onDelete}
                                onRowPrint={onPrint}
                                hidePrint={false}
                                onCSVExport={handleCSVExport}
                                onPDFExport={handlePDFExport}
                                enableTableSearch={false}
                                isLoading={isLoading}
                                autoPinActionColumn={false}
                                compactRows
                            />
                        </div>
                    </div>
                </ListPageLayout>

                <SalesListSettingsModal
                    show={showSettings}
                    allowedPlants={plants}
                    customers={[]}
                    initialSettings={salesSettings}
                    onSave={handleSettingsSave}
                    onClose={() => setShowSettings(false)}
                />
            </div>
        </div>
    );
}

export default GeneralJournalTable;
